import React, { useCallback, useEffect, useRef, useState } from 'react';
import QRCode from 'qrcode';
import { api } from '../services/api';
import { LocalDelivery, StoreCrew, StoreTour } from '../types';
import { brojUzRec, dana } from '../tekst';

interface Props {
  code: string;
}

/**
 * „Moj razvoz" — roba koju objekat vozi sam, svojim vozilom.
 *
 * ZAŠTO POSTOJI
 *   Prodavnica ume da kupcu odveze robu odmah, kad ne može da čeka tri dana na
 *   logistiku. Izmereno 14.09.2026: 92 takve otpremnice kroz osam objekata za
 *   četiri nedelje, bez ijednog traga. Neke od njih poslovođi stoje na spisku
 *   „Nije označeno kao isporučeno" sa napomenom „kod nas nema traga da je
 *   vožena" — što je bilo tačno.
 *
 * ŠTA JE DRUGAČIJE OD LOGISTIKE
 *   Roba je već u prodavnici. Nema izvora, nema dizanja iz magacina, nema
 *   „odakle se diže". Ostaju dva pitanja: gde kupac živi i ko vozi.
 *
 * KAKO TURA STIŽE DO RADNIKA
 *   QR sa ovog ekrana. Bez Vibera i bez poruke — u prodavnici svaki radnik ume
 *   da se zadesi za volanom, brojevi telefona se menjaju, a novi trgovci
 *   dolaze. Poslovođa okrene ekran, radnik skenira.
 */
export default function LocalDeliveryPanel({ code }: Props) {
  const [rows, setRows] = useState<LocalDelivery[]>([]);
  const [crew, setCrew] = useState<StoreCrew | null>(null);
  const [tours, setTours] = useState<StoreTour[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  /** Izabrane isporuke, REDOM kojim su izabrane — to je redosled obilaska. */
  const [izbor, setIzbor] = useState<string[]>([]);
  const [radnik, setRadnik] = useState('');
  const [vozilo, setVozilo] = useState<number | ''>('');

  /** Tura koja je upravo složena — njen QR stoji dok ga radnik ne skenira. */
  const [novaTura, setNovaTura] = useState<{ id: number; path: string; driver: string } | null>(null);

  const load = useCallback(async () => {
    try {
      const [spisak, posada, danasnje] = await Promise.all([
        api.localDeliveries(code),
        api.storeCrew(code),
        api.storeTours(code)
      ]);

      setRows(spisak);
      setCrew(posada);
      setTours(danasnje);
      setVozilo((v) => (v === '' ? posada.chosenVehicleId ?? '' : v));
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }, [code]);

  useEffect(() => { void load(); }, [load]);

  const slobodne = rows.filter((r) => !r.isOnTour);
  const spremne = slobodne.filter((r) => r.canBePlanned);
  const kocene = slobodne.filter((r) => !r.canBePlanned);

  function prebaci(kljuc: string) {
    setIzbor((trenutno) => trenutno.includes(kljuc)
      ? trenutno.filter((k) => k !== kljuc)
      : [...trenutno, kljuc]);
  }

  async function zapamtiVozilo(id: number) {
    setVozilo(id);
    try {
      await api.setStoreVehicle(code, id);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  async function slozi() {
    if (izbor.length === 0 || radnik === '' || vozilo === '') {
      return;
    }

    setBusy(true);
    try {
      const tura = await api.createStoreTour(code, {
        vehicleId: Number(vozilo),
        workerUserId: radnik,
        documentKeys: izbor
      });

      setNovaTura({ id: tura.id, path: tura.path, driver: tura.driver });
      setIzbor([]);
      setError(null);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      {error && <div className="health health--bad">{error}</div>}

      {novaTura && (
        <section className="panel panel--todo">
          <h2>Tura je složena — daj radniku da skenira</h2>
          <QrKod path={novaTura.path} driver={novaTura.driver} />
          <p className="actions">
            <button type="button" className="link" onClick={() => setNovaTura(null)}>
              Skenirano, skloni
            </button>
          </p>
        </section>
      )}

      <section className="panel">
        <h2>
          Za razvoz
          {spremne.length > 0 && <span className="count count--ceka">{spremne.length}</span>}
        </h2>

        <p className="note">
          Roba koju vozite vi, svojim vozilom. Ovde je nema iz magacina — sve što
          stoji na spisku već je kod vas. Označi koga voziš, <strong>redom kojim
          ćeš obilaziti</strong>, pa izaberi ko vozi i čime.
        </p>

        {slobodne.length === 0 ? (
          <p className="empty">
            Ništa ne čeka vaš razvoz. Ovde stižu računi sa oznakom 9999013, i ono
            što magacin prebaci na vas.
          </p>
        ) : (
          <ul className="storelist">
            {spremne.map((r) => {
              const mesto = izbor.indexOf(r.documentKey);

              return (
                <li key={r.documentKey}>
                  <label className="lokalni__red">
                    <input
                      type="checkbox"
                      checked={mesto >= 0}
                      onChange={() => prebaci(r.documentKey)}
                    />
                    <span className="storelist__where">
                      {mesto >= 0 && <span className="lokalni__mesto">{mesto + 1}.</span>}
                      {r.address}, {r.city}
                      <span className="muted"> · {r.customer}</span>
                      <span className="storelist__why muted">
                        {r.items.map((i) => `${i.articleName} × ${i.quantity}`).join(', ')}
                        {/* Šta se NE vozi, i zašto: logistika već predala, ili
                            kupac pristao da sačeka. Do 15.10.2026. se vozilo sve. */}
                        {r.vecIsporuceno && r.vecIsporuceno.length > 0
                          && ` · već isporučeno (logistika): ${r.vecIsporuceno.join(', ')}`}
                        {r.ostajeZaKasnije && r.ostajeZaKasnije.length > 0
                          && ` · ostaje za kasnije, po dogovoru: ${r.ostajeZaKasnije.join(', ')}`}
                        {r.requiresTwoPeople && ' · traži dva čoveka'}
                        {r.floor !== null && r.floor > 0
                          && ` · ${r.floor}. sprat${r.hasLift ? ', ima lift' : ', bez lifta'}`}
                        {r.movedByOperator && ` · prebacio ${r.movedBy ?? 'magacin'}`}
                        {r.moveNote && ` — „${r.moveNote}"`}
                      </span>
                    </span>
                  </label>

                  <span className={r.waitingDays > 2 ? 'flag flag--bad' : 'flag'}>
                    {r.waitingDays} {dana(r.waitingDays)}
                  </span>
                </li>
              );
            })}
          </ul>
        )}

        {kocene.length > 0 && (
          <>
            <p className="note">
              <strong>Ovo još ne može na turu.</strong> Najčešće je adresa —
              ispravi je na kartici „Šta me čeka" pa se vrati ovamo.
            </p>
            <ul className="storelist">
              {kocene.map((r) => (
                <li key={r.documentKey}>
                  <span className="storelist__where">
                    {r.address}, {r.city}
                    <span className="muted"> · {r.customer}</span>
                    <span className="storelist__why muted">{r.obstacle}</span>
                  </span>
                  <span className="flag flag--warn">ne može</span>
                </li>
              ))}
            </ul>
          </>
        )}
      </section>

      {izbor.length > 0 && crew && (
        <section className="panel panel--todo">
          <h2>Ko vozi</h2>

          <p className="note">
            Izabrano: <strong>{brojUzRec(izbor.length, 'isporuka', 'isporuke', 'isporuka')}</strong>,
            redom kojim si ih označio. Tura kreće iz objekta i vraća se u objekat.
          </p>

          <div className="lokalni__posada">
            <label>
              Vozi
              <select value={radnik} onChange={(e) => setRadnik(e.target.value)}>
                <option value="">— izaberi radnika —</option>
                {crew.workers.map((w) => (
                  <option key={w.userId} value={w.userId}>
                    {w.name} ({w.role.toLowerCase()})
                  </option>
                ))}
              </select>
            </label>

            <label>
              Vozilo
              <select
                value={vozilo}
                onChange={(e) => void zapamtiVozilo(Number(e.target.value))}
              >
                <option value="">— izaberi vozilo —</option>
                {crew.vehicles.map((v) => (
                  <option key={v.id} value={v.id}>
                    {v.name}{v.registration ? ` · ${v.registration}` : ''}
                  </option>
                ))}
              </select>
            </label>
          </div>

          {crew.chosenBy && (
            <p className="note">
              Vozilo se pamti — sledeći put je već izabrano. Poslednji put ga je
              upisao {crew.chosenBy}.
            </p>
          )}

          <button
            type="button"
            className="primary"
            disabled={busy || radnik === '' || vozilo === ''}
            onClick={() => void slozi()}
          >
            {busy ? 'Slažem…' : 'Složi turu i pokaži QR'}
          </button>
        </section>
      )}

      {tours.length > 0 && (
        <section className="panel">
          <h2>Ture <span className="muted">· {tours.length}</span></h2>

          {tours.some((t) => t.stara) && (
            <p className="note">
              Ture označene kao <strong>ostala otvorena</strong> drže svoju robu
              rezervisanom — dok se ne zatvore, ta roba se ne nudi ni za nov
              razvoz. Daj radniku QR da je otvori i pritisne „Završi turu".
            </p>
          )}

          <ul className="storelist">
            {tours.map((t) => (
              <li key={t.id}>
                <span className="storelist__where">
                  {t.name}
                  <span className="muted">
                    {' '}· {t.driver ?? 'bez vozača'}
                    {t.vehicle ? ` · ${t.vehicle}` : ''}
                  </span>
                  <span className="storelist__why muted">
                    {t.delivered} / {t.stops} obiđeno
                  </span>
                </span>

                {t.stara ? (
                  <span className="flag flag--bad">
                    ostala otvorena · {new Date(t.date).toLocaleDateString('sr-RS')}
                  </span>
                ) : (
                  <span className={t.actualEnd ? 'flag flag--ok' : t.actualStart ? 'flag' : 'flag flag--warn'}>
                    {t.actualEnd ? 'gotova' : t.actualStart ? 'na putu' : 'nije krenula'}
                  </span>
                )}

                {/* Radnik ume da zatvori pregledač ili uzme drugi telefon.
                    QR se zato ne pokazuje jednom nego stoji dok tura traje. */}
                {!t.actualEnd && t.path && (
                  <details className="lokalni__qr">
                    <summary>Pokaži QR</summary>
                    <QrKod path={t.path} driver={t.driver ?? ''} />
                  </details>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}
    </>
  );
}

/**
 * QR koji radnik skenira da bi mu se tura otvorila u telefonu.
 *
 * Domaćin se sklapa OVDE, u pregledaču — server ne zna sa koje je adrese ekran
 * otvoren, a pogrešan domaćin daje kod koji vodi nikuda.
 */
function QrKod({ path, driver }: { path: string; driver: string }) {
  const platno = useRef<HTMLCanvasElement>(null);
  const pun = `${window.location.origin}${path}`;

  useEffect(() => {
    if (platno.current) {
      void QRCode.toCanvas(platno.current, pun, { width: 220, margin: 1 });
    }
  }, [pun]);

  return (
    <div className="lokalni__kod">
      <canvas ref={platno} />
      <p className="note">
        {driver ? <><strong>{driver}</strong> — skeniraj </> : 'Skeniraj '}
        telefonom kojim ćeš voziti. Tura se učita cela i radi bez signala.
      </p>
    </div>
  );
}
